// Export guard — the browser half of the export lock.
//
// Forty pages build files in the browser (CSV from loaded rows, label sheets,
// workbooks) and hand them over through an <a download> click, which never
// touches the server. Every one of those clicks passes through here, so the
// rule the server enforces on its own files holds for these too. The policy
// comes from GET /api/export-policy after sign-in; until it arrives nothing
// is blocked, and the server remains the authority for anything it serves.
const DATA_EXT = /\.(csv|tsv|xlsx?|xlsm|json|ndjson|zip|txt|xml|sql)\b/i;
const DOC_EXT = /\.(pdf|png|jpe?g|gif|webp|svg|zpl|prn|html?|mp4|mp3|wav|m4a|docx?|pptx?)\b/i;

let policy = { canExportData: true, canExportDocuments: true, reason: '' };
let installed = false;

export function setExportPolicy(next) {
  policy = { ...policy, ...(next || {}) };
}

export function getExportPolicy() { return policy; }

/** 'data' | 'document' | null from a download name or href. */
export function kindOf(name, href) {
  const s = `${name || ''} ${href || ''}`;
  if (DATA_EXT.test(s)) return 'data';
  if (DOC_EXT.test(s)) return 'document';
  if (/^(blob:|data:text\/csv|data:application\/json)/i.test(String(href || ''))) return 'data';
  return null;
}

export function mayDownload(name, href) {
  const kind = kindOf(name, href);
  if (kind === 'data') return policy.canExportData;
  if (kind === 'document') return policy.canExportDocuments;
  return true;
}

function refuse() {
  const msg = policy.reason || 'Exports are restricted to the founder.';
  try { window.alert(msg); } catch { /* no window */ }
}

function guardAnchor(a) {
  if (!a || !a.hasAttribute || !a.hasAttribute('download')) return true;
  if (mayDownload(a.getAttribute('download'), a.href)) return true;
  refuse();
  return false;
}

export function installExportGuard() {
  if (installed || typeof window === 'undefined' || !window.HTMLAnchorElement) return;
  installed = true;
  const proto = window.HTMLAnchorElement.prototype;
  const origClick = proto.click;
  // Programmatic downloads: a.click() from a page's export handler.
  proto.click = function click(...args) {
    if (!guardAnchor(this)) return undefined;
    return origClick.apply(this, args);
  };
  // Real clicks on rendered <a download> links, caught before React sees them.
  window.document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[download]') : null;
    if (a && !guardAnchor(a)) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
}

export const _resetForTests = () => { policy = { canExportData: true, canExportDocuments: true, reason: '' }; };
